// Models tab — browse + learn about OpenRouter models. Captain's
// 2026-04-23 ask: "add a separate Models tab for browsing and learning
// about Models, where the right drawer action there is to click on
// and talk to one." Personas tab is for user-created personas only;
// the model roster lives here.
//
// Click a model row → the shared right drawer (owned by
// ProjectRoomPage) swaps to a one-shot chat scoped to that model.
// The caller's OpenRouter BYOK key powers the call server-side.
//
// Coral audit (2026-04-23):
// - Ship. Operational ergonomics good — search + sort + stats +
//   click-to-talk. No decoration creep, no apology-loop copy.
// - Evidence beside claim: each row shows price / age / modality /
//   context size inline. Description clipped to 2 lines so rows stay
//   scannable.
// - Semantic tokens throughout; no raw hex. No uppercase or wide-
//   tracking. Hover is border-colour change only, no translate/scale.
// - Grouped by provider so the eye can scan by family.

import { TabScrollbox } from "../../chrome/TabScrollbox";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  fetchOpenRouterModels,
  formatModelAge,
  formatModelModality,
  formatModelPrice,
  type OpenRouterModel,
} from "../../api/openrouter";
import { HarnessScoresPanel } from "../HarnessScoresPanel";
import { ShadowRunsPanel } from "../ShadowRunsPanel";
import type { DrawerContent } from "./drawerContent";

interface Props {
  workspaceId: number;
  orgId: number | null;
  onOpenDrawerContent: (content: DrawerContent) => void;
}

type Sort =
  | "vendor"
  | "alpha"
  | "price-low"
  | "price-high"
  | "age-new"
  | "age-old";

export function ModelsTab({ workspaceId, orgId, onOpenDrawerContent }: Props) {
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [query, setQuery] = useState("");
  /** Vendor = group by provider prefix (openai/, anthropic/, …)
   * with collapsible sections. Any other sort renders a flat list. */
  const [sort, setSort] = useState<Sort>("vendor");
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState("");
  // Provider sections auto-collapse by default so the tab opens
  // quiet and scannable (Captain 2026-04-23). A non-empty search
  // forces everything open so filtered hits are visible without
  // needing the Captain to expand.
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(
    () => new Set(),
  );
  function toggleProvider(provider: string) {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  }

  useEffect(() => {
    fetchOpenRouterModels()
      .then(setModels)
      .catch((e: unknown) =>
        setLoadErr(
          e instanceof Error
            ? e.message
            : "Could not load the model list. Retry in a moment.",
        ),
      );
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? models.filter(
          (m) =>
            m.id.toLowerCase().includes(q) ||
            (m.name ?? "").toLowerCase().includes(q) ||
            (m.description ?? "").toLowerCase().includes(q),
        )
      : models;
    const sorted = [...base];
    switch (sort) {
      case "vendor":
      case "alpha":
        // Vendor sort still orders models alphabetically within
        // each group; the group layout is applied at render time.
        sorted.sort((a, b) => a.id.localeCompare(b.id));
        break;
      case "price-low":
      case "price-high": {
        const rank = (m: OpenRouterModel) => {
          const p = m.pricing?.prompt ?? "";
          const n = Number(p);
          return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
        };
        sorted.sort((a, b) =>
          sort === "price-low" ? rank(a) - rank(b) : rank(b) - rank(a),
        );
        break;
      }
      case "age-new":
      case "age-old": {
        const rank = (m: OpenRouterModel) => m.created ?? 0;
        sorted.sort((a, b) =>
          sort === "age-new" ? rank(b) - rank(a) : rank(a) - rank(b),
        );
        break;
      }
    }
    return sorted;
  }, [models, query, sort]);

  const groups = useMemo(() => {
    const byProvider = new Map<string, OpenRouterModel[]>();
    for (const m of filtered) {
      const provider = m.id.split("/")[0] ?? "other";
      const bucket = byProvider.get(provider) ?? [];
      bucket.push(m);
      byProvider.set(provider, bucket);
    }
    return Array.from(byProvider.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [filtered]);

  function pickModel(m: OpenRouterModel) {
    setPickedId(m.id);
    onOpenDrawerContent({
      kind: "model",
      modelId: m.id,
      modelName: m.name ?? m.id,
      description: m.description,
    });
  }

  return (
    <TabScrollbox>
      <div className="w-full">
        <header className="mb-4">
          <p className="text-xs text-muted">
            Browse every model available through your OpenRouter key. Click
            any row to talk to it directly — ephemeral, no project
            attachment. Shore is on the drawer by default for comparisons.
          </p>
          {orgId == null && (
            <p className="mt-2 text-xs text-warning">
              No org is resolved for this project yet — click-to-chat will
              need one.
            </p>
          )}
        </header>

        <div className="mb-6 grid grid-cols-1 gap-4">
          <ShadowRunsPanel workspaceId={workspaceId} />
          <HarnessScoresPanel workspaceId={workspaceId} />
        </div>

        <section className="mb-4 flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 border-b border-edge pb-1">
            <h3 className="text-sm font-semibold text-primary">
              Model browser
            </h3>
            <span className="text-[0.625rem] text-faint">
              talk to any lane in the drawer
            </span>
          </div>
          <p className="text-[0.625rem] text-muted">
            Use the panels above to compare outcomes. Use the browser below
            when you want to inspect or chat with a specific model directly.
          </p>
        </section>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search models…"
            className="flex-1 min-w-[12rem] rounded-md border border-edge bg-surface-raised px-3 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
            data-testid="models-search"
          />
          <label className="flex items-center gap-2 text-xs text-muted">
            Sort
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="cursor-pointer rounded-md border border-edge bg-surface-raised px-2 py-1 text-xs text-primary outline-none focus:border-border-highlight"
              data-testid="models-sort"
            >
              <option value="vendor">Vendor</option>
              <option value="alpha">A → Z</option>
              <option value="age-new">Newest</option>
              <option value="age-old">Oldest</option>
              <option value="price-low">Cheapest</option>
              <option value="price-high">Priciest</option>
            </select>
          </label>
          <span className="font-code text-xs text-faint">
            {filtered.length} / {models.length}
          </span>
        </div>

        {loadErr && (
          <p className="mb-4 text-xs text-danger" data-testid="models-load-error">
            {loadErr}
          </p>
        )}

        <div className="flex flex-col gap-3" data-testid="models-list">
          {sort === "vendor" ? (
            <>
              {groups.map(([provider, rows]) => {
                const isExpanded =
                  query.trim() !== "" || expandedProviders.has(provider);
                return (
                  <section key={provider} className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => toggleProvider(provider)}
                      className="flex cursor-pointer items-center gap-2 border-b border-edge pb-1 text-left transition-colors hover:border-accent"
                      data-testid={`models-provider-${provider}`}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <ChevronDown size={12} className="text-muted" />
                      ) : (
                        <ChevronRight size={12} className="text-muted" />
                      )}
                      <h3 className="text-xs font-semibold text-primary">
                        {provider}
                      </h3>
                      <span className="font-code text-[0.625rem] text-faint">
                        {rows.length}
                      </span>
                    </button>
                    {isExpanded && (
                      <ul className="flex flex-col gap-2">
                        {rows.map((m) => (
                          <ModelRow
                            key={m.id}
                            model={m}
                            picked={pickedId === m.id}
                            onPick={pickModel}
                          />
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
              {groups.length === 0 && !loadErr && (
                <div className="rounded-md border border-edge bg-surface-raised px-3 py-4 text-center text-xs text-muted">
                  No models match that search.
                </div>
              )}
            </>
          ) : (
            <>
              {filtered.length === 0 && !loadErr && (
                <div className="rounded-md border border-edge bg-surface-raised px-3 py-4 text-center text-xs text-muted">
                  No models match that search.
                </div>
              )}
              <ul className="flex flex-col gap-2">
                {filtered.map((m) => (
                  <ModelRow
                    key={m.id}
                    model={m}
                    picked={pickedId === m.id}
                    onPick={pickModel}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </TabScrollbox>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-1 text-faint">
      <span>{label}</span>
      <span className="text-muted">{value}</span>
    </span>
  );
}

function ModelRow({
  model,
  picked,
  onPick,
}: {
  model: OpenRouterModel;
  picked: boolean;
  onPick: (m: OpenRouterModel) => void;
}) {
  return (
    <li
      className={`rounded-md border border-edge bg-surface-raised px-3 py-2 transition-colors hover:border-accent ${
        picked ? "border-accent" : ""
      }`}
      data-testid={`models-row-${model.id}`}
    >
      <button
        type="button"
        onClick={() => onPick(model)}
        className="flex w-full cursor-pointer flex-col gap-1 text-left"
      >
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-code text-sm text-primary">{model.id}</span>
          {model.name && model.name !== model.id && (
            <span className="text-xs text-muted">{model.name}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.625rem] font-code">
          <Stat label="Price" value={formatModelPrice(model) ?? "—"} />
          <Stat label="Age" value={formatModelAge(model) ?? "—"} />
          <Stat
            label="Modality"
            value={formatModelModality(model) ?? "—"}
          />
          <Stat
            label="Context"
            value={
              model.context_length
                ? `${model.context_length.toLocaleString()} tok`
                : "—"
            }
          />
        </div>
        {model.description && (
          <p className="line-clamp-2 text-xs text-muted">{model.description}</p>
        )}
      </button>
    </li>
  );
}
